/**
 * ProfileSetupPage - Restaurant profile configuration page
 * Handles restaurant profile setup with proper validation and error handling
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { getCurrencyForCountryCode } from '@data/countries-cities';
import { useCountryCitySelection } from '@hooks/useCountryCitySelection';

import { FormField } from '@components/forms/FormField';
import { SelectField } from '@components/forms/SelectField';
import { Button } from '@components/ui/Button';
import { useAuthStore } from '@stores/authStore';
import { apiService } from '@services/api';
import { RestaurantProfileForm } from '@defs/forms';
import { CUISINE_TYPE_OPTIONS } from '@defs/forms';

// ============================================================================
// Validation Schema
// ============================================================================

const profileSchema = yup.object({
  restaurant_name: yup
    .string()
    .min(2, 'El nombre del restaurante debe tener al menos 2 caracteres')
    .max(100, 'El nombre del restaurante no puede tener más de 100 caracteres')
    .required('Nombre del restaurante es requerido')
    .trim(),
  country: yup
    .string()
    .length(2, 'Código de país debe tener exactamente 2 caracteres')
    .matches(/^[A-Z]{2}$/, 'Código de país debe ser válido (ej: ES, FR, IT)')
    .required('País es requerido'),
  city: yup
    .string()
    .min(2, 'Región/Provincia/Estado debe tener al menos 2 caracteres')
    .max(100, 'Región/Provincia/Estado no puede tener más de 100 caracteres')
    .required('Región/Provincia/Estado es requerida')
    .trim(),
  address: yup
    .string()
    .max(255, 'La dirección no puede tener más de 255 caracteres')
    .trim()
    .optional(),
  cuisine_type: yup.string().required('Tipo de cocina es requerido'),
  daily_covers: yup
    .number()
    .typeError('Debe ser un número válido')
    .positive('Debe ser un número positivo')
    .integer('Debe ser un número entero')
    .min(1, 'Debe ser al menos 1 cubierto')
    .max(10000, 'No puede ser más de 10,000 cubiertos')
    .required('Número de cubiertos diarios es requerido'),
  currency: yup
    .string()
    .length(3, 'Código de moneda debe tener 3 caracteres')
    .required('Moneda es requerida'),
});

// ============================================================================
// Styled Components
// ============================================================================

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`;

// ============================================================================
// Component
// ============================================================================

const CUSTOM_REGION_OPTION_VALUE = '__custom_region__';
const CUSTOM_REGION_OPTION_LABEL = 'Otro / Escribir…';

export const ProfileSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, setProfile, isLoading: authLoading } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState(false);
  const [isCustomRegion, setIsCustomRegion] = useState(false);
  const customRegionInputRef = useRef<HTMLInputElement | null>(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RestaurantProfileForm>({
    resolver: yupResolver(profileSchema) as any,
    mode: 'onBlur',
    defaultValues: {
      country: 'ES', // Default to Spain (using country code)
      currency: 'EUR', // Default to EUR (will be updated based on country)
    },
  });

  // Country/City selection hook
  const {
    cities,
    selectedCountry,
    setSelectedCountry,
    selectedCity,
    setSelectedCity,
    filteredCountries,
    filteredCities,
    resetCitySelection,
  } = useCountryCitySelection({
    defaultCountry: 'ES',
    enableSearch: true,
  });

  // Watch country changes to automatically update currency
  const watchedCountry = watch('country');

  // Single unified effect for country/city state synchronization
  useEffect(() => {
    // Only proceed if we have a valid country change
    if (!watchedCountry) return;

    // Check if country actually changed to prevent unnecessary updates
    if (watchedCountry === selectedCountry) return;

    // Update country-related state
    setSelectedCountry(watchedCountry);
    setIsCustomRegion(false);
    const currencyCode = getCurrencyForCountryCode(watchedCountry);
    setValue('currency', currencyCode, { shouldValidate: false });

    // Reset city selection when country changes (but not on initial load)
    if (selectedCountry && selectedCity) {
      resetCitySelection();
      setValue('city', '', { shouldValidate: false });
    }
  }, [
    watchedCountry,
    selectedCountry,
    selectedCity,
    setSelectedCountry,
    resetCitySelection,
    setValue,
  ]);

  // Check if user already has a profile (redirect if so)
  useEffect(() => {
    // Only check profile if user is authenticated and auth is not loading
    if (!user || authLoading || isCheckingProfile) return;

    // If we already have a profile from auth store, redirect immediately
    if (profile) {
      navigate('/', { replace: true });
      return;
    }

    // If profile is null (meaning we've checked and it doesn't exist), continue with setup
    if (profile === null) {
      setIsCheckingProfile(false);
      return;
    }

    const checkExistingProfile = async () => {
      setIsCheckingProfile(true);
      try {
        const existingProfile = await apiService.getRestaurantProfile();
        if (existingProfile) {
          toast.success(
            'Ya tienes un perfil configurado. Redirigiendo al dashboard...',
          );
          navigate('/', { replace: true });
        } else {
          // No profile exists, set profile to null to indicate we've checked
          setProfile(null);
        }
      } catch {
        // Profile doesn't exist, set to null to indicate we've checked
        setProfile(null);
      } finally {
        setIsCheckingProfile(false);
      }
    };

    checkExistingProfile();
  }, [user, profile, authLoading, isCheckingProfile, navigate, setProfile]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      toast.error('Debes iniciar sesión para configurar tu perfil.');
      navigate('/auth/login', { replace: true });
    }
  }, [authLoading, user, navigate]);

  const onSubmit = async (data: RestaurantProfileForm) => {
    setIsSubmitting(true);

    try {
      // Ensure CSRF token is available
      await apiService.csrf();

      const result = await apiService.createRestaurantProfile(data);

      // Update auth store with new profile
      setProfile(result.profile);

      toast.success('¡Perfil del restaurante creado exitosamente!');

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Profile creation error:', error);
      const apiError = error as {
        status?: number;
        detail?: string;
        message?: string;
      };

      // Handle specific error cases
      if (apiError.status === 409) {
        toast.error('Ya tienes un perfil configurado. Redirigiendo...');
        navigate('/', { replace: true });
      } else if (apiError.status === 422) {
        toast.error(
          'Datos inválidos. Verifica la información e intenta de nuevo.',
        );
      } else if (apiError.detail) {
        toast.error(apiError.detail);
      } else {
        toast.error('Error al crear el perfil. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render if user is not authenticated or still checking profile
  if (!user || authLoading || isCheckingProfile) {
    return null;
  }

  return (
    <>
      <h1 className="auth-title">Configura tu restaurante</h1>
      <p className="auth-subtitle">
        Completa la información de tu restaurante para comenzar
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormField
          label="Nombre del restaurante"
          name="restaurant_name"
          type="text"
          placeholder="El nombre de tu restaurante"
          required
          autoComplete="organization"
          register={register}
          error={errors.restaurant_name}
        />

        <Controller
          name="country"
          control={control}
          render={({ field, fieldState }) => (
            <SelectField
              name="country"
              label="País"
              options={filteredCountries}
              value={field.value}
              onChange={(value) => {
                const stringValue = Array.isArray(value) ? value[0] : value;
                // Only update form state - useEffect will handle hook state sync
                field.onChange(stringValue);
              }}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              required
              searchable
              placeholder="Selecciona un país"
            />
          )}
        />

        <Controller
          name="city"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <SelectField
                id="profile-setup-city-select"
                name="city"
                label="Región / Provincia / Estado"
                options={[
                  ...(!isCustomRegion &&
                  typeof field.value === 'string' &&
                  field.value.trim() !== '' &&
                  !filteredCities.some((opt) => opt.value === field.value)
                    ? [{ value: field.value, label: field.value }]
                    : []),
                  ...filteredCities,
                  {
                    value: CUSTOM_REGION_OPTION_VALUE,
                    label: CUSTOM_REGION_OPTION_LABEL,
                  },
                ]}
                value={
                  isCustomRegion
                    ? CUSTOM_REGION_OPTION_VALUE
                    : (field.value ?? '')
                }
                onChange={(value) => {
                  const stringValue = Array.isArray(value)
                    ? value[0]
                    : (value ?? '');

                  if (stringValue === CUSTOM_REGION_OPTION_VALUE) {
                    setIsCustomRegion(true);
                    setTimeout(() => {
                      customRegionInputRef.current?.focus();
                    }, 0);
                    return;
                  }

                  setIsCustomRegion(false);

                  // Update form state and hook state together for regions
                  field.onChange(stringValue);
                  setSelectedCity(stringValue || undefined);
                }}
                onBlur={field.onBlur}
                error={isCustomRegion ? undefined : fieldState.error?.message}
                required
                searchable
                placeholder={
                  selectedCountry
                    ? 'Selecciona una región/provincia/estado'
                    : 'Selecciona un país primero'
                }
                disabled={!selectedCountry || cities.length === 0}
              />

              {isCustomRegion && (
                <FormField
                  id="profile-setup-city-custom"
                  label="Otra región / provincia / estado"
                  name="city"
                  type="text"
                  placeholder="Escribe una región/provincia/estado"
                  required
                  value={typeof field.value === 'string' ? field.value : ''}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    setSelectedCity(e.target.value || undefined);
                  }}
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                  ref={(el) => {
                    customRegionInputRef.current = el;
                    field.ref(el);
                  }}
                />
              )}
            </>
          )}
        />

        <FormField
          label="Dirección"
          name="address"
          type="text"
          placeholder="Dirección completa del restaurante"
          register={register}
          error={errors.address}
        />

        <FormField
          label="Sitio web"
          name="website"
          type="text"
          placeholder="https://www.mirestaurante.com"
          register={register}
          error={errors.website}
        />

        <SelectField
          label="Tipo de cocina"
          name="cuisine_type"
          options={CUISINE_TYPE_OPTIONS}
          placeholder="Selecciona el tipo de cocina"
          required
          searchable
          control={control}
          error={errors.cuisine_type}
        />

        <FormField
          label="Número promedio de cubiertos diarios"
          name="daily_covers"
          type="number"
          placeholder="100"
          required
          min={1}
          max={10000}
          register={register}
          registerOptions={{
            valueAsNumber: true,
          }}
          error={errors.daily_covers}
          helperText="Ayuda a calcular métricas de sostenibilidad por comensal"
        />

        <FormActions>
          <Button
            type="submit"
            variant="primary"
            size="md"
            fullWidth
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            Completar configuración
          </Button>
        </FormActions>
      </form>
    </>
  );
};
